// src/components/books/StepQuizUI.jsx
// ================================================================
import React, { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';

// ================================================================
// INTRO
// ================================================================
export function StepQuizIntro({ quiz, step, progress, canAttempt, onStart, onBack }) {
  const attemptsCount = progress?.attempts?.length || 0;
  const maxAttempts = quiz.maxAttempts || 2;
  const unlockedCount = progress?.unlockedWithPayment?.length || 0;
  // 🆕 هر پرداخت = ۱ تلاش اضافه
  const totalAllowed = maxAttempts + unlockedCount;
  const attemptsLeft = Math.max(0, totalAllowed - attemptsCount);
  const retryPrice = quiz.retryPrice || 10000;
  const passThreshold = quiz.passThreshold || 12;

  return (
    <div className="step-quiz-intro">
      <header className="step-quiz-header">
        <button onClick={onBack} className="step-quiz-back-btn">← بازگشت</button>
      </header>

      <div className="step-quiz-intro-card">
        <div className="step-quiz-intro-icon">📝</div>
        <h1 className="step-quiz-intro-title">{step?.title || 'آزمون'}</h1>
        <p className="step-quiz-intro-sub">آزمون سنجش یادگیری</p>

        <div className="step-quiz-intro-stats">
          <div className="step-quiz-stat-item">
            <span className="step-quiz-stat-icon">📊</span>
            <span className="step-quiz-stat-value">{quiz.questions?.length || 0}</span>
            <span className="step-quiz-stat-label">سوال</span>
          </div>
          <div className="step-quiz-stat-item">
            <span className="step-quiz-stat-icon">⏱️</span>
            <span className="step-quiz-stat-value">{quiz.duration || 0}</span>
            <span className="step-quiz-stat-label">دقیقه</span>
          </div>
          <div className="step-quiz-stat-item">
            <span className="step-quiz-stat-icon">🎯</span>
            <span className="step-quiz-stat-value">{passThreshold}</span>
            <span className="step-quiz-stat-label">حد قبولی از ۲۰</span>
          </div>
          <div className="step-quiz-stat-item">
            <span className="step-quiz-stat-icon">🔄</span>
            <span className="step-quiz-stat-value">{attemptsLeft}</span>
            <span className="step-quiz-stat-label">تلاش باقی‌مانده</span>
          </div>
        </div>

        <div className="step-quiz-intro-warning">
          <p>
            ⚠️ برای تکمیل این مرحله باید حداقل نمره{' '}
            <strong>{passThreshold} از ۲۰</strong> کسب کنید.
          </p>
          <p>
            ℹ️ حداکثر <strong>{maxAttempts} بار</strong> می‌توانید در این آزمون شرکت کنید.
            {unlockedCount > 0 && ` (با ${unlockedCount} بار پرداخت، ${unlockedCount} تلاش اضافه دارید)`}
          </p>
          {attemptsCount >= maxAttempts && attemptsCount < totalAllowed && (
            <p>💳 شما الان در تلاش پولی هستید. باقی‌مانده: <strong>{attemptsLeft}</strong> تلاش.</p>
          )}
          {attemptsCount >= maxAttempts && unlockedCount === 0 && (
            <p>💳 در صورت رد شدن در هر دو تلاش، برای هر تلاش مجدد باید <strong>{retryPrice.toLocaleString('fa-IR')} تومان</strong> پرداخت کنید.</p>
          )}
        </div>

        <button className="step-quiz-start-btn" onClick={onStart}>
          🚀 {attemptsCount > 0 ? 'تلاش مجدد' : 'شروع آزمون'}
        </button>
      </div>
    </div>
  );
}

// ================================================================
// RESULT
// ================================================================
export function StepQuizResult({
  quiz, step, progress, canAttempt, result, onBack, onRetry,
}) {
  const passed = !!progress?.passed || !!result?.passed;
  const bestScore = progress?.bestScore ?? result?.score ?? 0;
  const attempts = progress?.attempts || [];
  const lastAttempt = attempts[attempts.length - 1];
  const lastScore = lastAttempt?.score ?? result?.score ?? 0;
  const passThreshold = quiz.passThreshold || 12;
  const attemptsCount = attempts.length;
  const maxAttempts = quiz.maxAttempts || 2;
  const unlockedCount = progress?.unlockedWithPayment?.length || 0;
  const totalAllowed = maxAttempts + unlockedCount;
  const canRetry = canAttempt?.allowed !== false;
  const percentage = result?.percentage ?? (
    result?.maxScore > 0 ? Math.round((lastScore / result.maxScore) * 100) : 0
  );

  // جزئیات سوالات (اگر کارنامه موجود باشد)
  const details = result?.details || [];
  const hasDetails = Array.isArray(details) && details.length > 0;

  useEffect(() => {
    if (passed) {
      try { confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } }); } catch {}
    }
  }, [passed]);

  return (
    <div className={`step-quiz-result ${passed ? 'passed' : 'failed'}`}>
      <div className="step-quiz-result-card step-quiz-result-card-wide">
        {/* ═══ هدر ═══ */}
        <div className="step-quiz-result-icon">{passed ? '🎉' : '📚'}</div>
        <h2 className="step-quiz-result-title">
          {passed ? 'تبریک! قبول شدید' : 'متأسفانه قبول نشدید'}
        </h2>

        {/* ═══ دایره‌ی نمره ═══ */}
        <div className="step-quiz-result-score">
          <span className="step-quiz-result-score-num">
            {passed ? bestScore : lastScore}
          </span>
          <span className="step-quiz-result-score-total">
            از {result?.maxScore || 20}
          </span>
        </div>

        {percentage > 0 && (
          <div className="step-quiz-result-percent">{percentage}%</div>
        )}

        <p className="step-quiz-result-message">
          {passed
            ? `بهترین نمره‌ی شما: ${bestScore} از ${result?.maxScore || 20} — این مرحله برای شما تکمیل شد.`
            : `حد قبولی ${passThreshold} از ${result?.maxScore || 20} است. تلاش‌های انجام‌شده: ${attemptsCount} از ${totalAllowed}`}
        </p>

        {/* ═══ کارنامه‌ی تفصیلی ═══ */}
        {hasDetails && (
          <div className="step-quiz-result-details">
            <h3 className="step-quiz-result-details-title">📋 کارنامه‌ی تفصیلی</h3>
            <div className="step-quiz-result-details-table-wrap">
              <table className="step-quiz-result-details-table">
                <thead>
                  <tr>
                    <th>سوال</th>
                    <th>نوع</th>
                    <th>وضعیت</th>
                    <th>نمره</th>
                  </tr>
                </thead>
                <tbody>
                  {details.map((d, idx) => {
                    const isEssay = d.isEssay || d.type === 'essay' || d.type === 'file';
                    let status = '';
                    let statusColor = '';
                    let statusIcon = '';
                    if (isEssay) {
                      if (d.isCorrect === true) { status = 'صحیح'; statusColor = '#34d399'; statusIcon = '✅'; }
                      else if (d.isCorrect === false) { status = 'غلط'; statusColor = '#ef4444'; statusIcon = '❌'; }
                      else { status = 'در انتظار تصحیح'; statusColor = '#f59e0b'; statusIcon = '⏳'; }
                    } else {
                      if (d.isCorrect === true) { status = 'صحیح'; statusColor = '#34d399'; statusIcon = '✅'; }
                      else { status = 'غلط'; statusColor = '#ef4444'; statusIcon = '❌'; }
                    }
                    const score = isEssay && d.isCorrect === null ? '...' : (d.score ?? 0);
                    const maxScore = d.maxScore ?? 0;
                    return (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{isEssay ? '📝 تشریحی' : '🎯 تستی'}</td>
                        <td style={{ color: statusColor }}>{statusIcon} {status}</td>
                        <td>{score} / {maxScore}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══ دکمه‌ها ═══ */}
        {!passed && canRetry && (
          <button
            className="step-quiz-result-retry-btn"
            onClick={onRetry}
          >
            🔄 تلاش مجدد ({totalAllowed - attemptsCount} باقی‌مانده)
          </button>
        )}

        {!passed && !canRetry && (
          <p className="step-quiz-result-exhausted">
            ⚠️ تلاش‌های شما به پایان رسیده است. برای تلاش مجدد باید پرداخت کنید.
          </p>
        )}

        <button className="step-quiz-result-back-btn" onClick={onBack}>
          بازگشت به درس
        </button>
      </div>
    </div>
  );
}

// ================================================================
// PAYWALL
// ================================================================
export function StepQuizPaywall({ quiz, progress, canAttempt, onPay, onBack }) {
  const price = quiz.retryPrice || 10000;
  const [paying, setPaying] = useState(false);
  const attemptsCount = progress?.attempts?.length || 0;
  const maxAttempts = quiz.maxAttempts || 2;
  const passThreshold = quiz.passThreshold || 12;
  const unlockedCount = progress?.unlockedWithPayment?.length || 0;
  // 🆕 برای نمایش پیام مناسب
  const totalAllowed = maxAttempts + unlockedCount;

  const handlePay = async () => {
    setPaying(true);
    try { await onPay(); } finally { setPaying(false); }
  };

  return (
    <div className="step-quiz-paywall">
      <div className="step-quiz-paywall-card">
        <div className="step-quiz-paywall-icon">🔒</div>
        <h2>قفل آزمون</h2>
        <p>شما {attemptsCount} بار در این آزمون شرکت کرده‌اید و متأسفانه قبول نشدید.</p>
        <p>حد قبولی این آزمون {passThreshold} از ۲۰ است.</p>
        <p>
          {unlockedCount > 0
            ? `شما تاکنون ${unlockedCount} بار پرداخت کرده‌اید.`
            : 'تلاش‌های رایگان شما به پایان رسیده است.'}
        </p>
        <p>برای شرکت مجدد، لطفاً مبلغ زیر را پرداخت کنید:</p>

        <div className="step-quiz-paywall-price">
          <span className="step-quiz-paywall-price-num">{price.toLocaleString('fa-IR')}</span>
          <span className="step-quiz-paywall-price-unit">تومان</span>
        </div>

        <button
          className="step-quiz-paywall-pay-btn"
          onClick={handlePay}
          disabled={paying}
        >
          {paying ? '⏳ در حال پردازش...' : '💳 پرداخت و شرکت مجدد'}
        </button>

        <button className="step-quiz-paywall-back-btn" onClick={onBack}>
          انصراف و بازگشت
        </button>
      </div>
    </div>
  );
}